import { memberRegistrationSpecs } from "../../member/memberFields";
import { packageVoiceSchema } from "../../package/voiceAddSchema";
import { staffVoiceSchema } from "../../staff/voiceAddSchema";
import { gateVoiceSchema } from "../../gate/voiceAddSchema";
import { courtVoiceSchema } from "../../court/voiceAddSchema";
import { followupVoiceSchema } from "../../followupProgram/voiceAddSchema";
import { trainerVoiceSchema } from "../../trainer/voiceAddSchema";
import { wodVoiceSchema } from "../../wod/voiceAddSchema";
import { leadVoiceSchema } from "../../lead/voiceAddSchema";
import { retailVoiceSchema } from "../../retail/voiceAddSchema";
import { saleVoiceSchema } from "../../sale/voiceAddSchema";
import { classVoiceSchema } from "../../class/voiceAddSchema";
import { workshopVoiceSchema } from "../../class/workshopVoiceSchema";
import { AppointmentSchema } from "../../appointment/utils";
import { fieldsFromSchema } from "../readSchemaFields";
import {
  applyAppointmentVoiceFields,
  applyPackageVoiceFields,
  applyScheduledVoiceFields,
} from "../voiceFieldDefaults";

export const LOOKUP_BY_KEY = {
  packages: { source: "package", multiple: true },
  assignedPackages: { source: "package", multiple: true },
  products: { source: "product", multiple: true },
  trainer: { source: "instructor" },
  trainers: { source: "instructor", multiple: true },
  member: { source: "member" },
  members: { source: "member", multiple: true },
  class: { source: "class" },
  classes: { source: "class", multiple: true },
  appointment: { source: "appointment" },
  appointments: { source: "appointment", multiple: true },
};

const formInput = (field) =>
  /image|picture|photo|file/i.test(field.key)
    ? { ...field, input: "form" }
    : field;

const withVoiceBehavior = (fields) =>
  fields.map((field) => {
    if (field.key === "phone" || field.key.endsWith(".phone")) {
      return { ...field, format: "internationalPhone" };
    }
    if (field.key === "email" || field.key.endsWith(".email")) {
      return { ...field, format: "email" };
    }
    if (field.key === "gender" || field.key.endsWith(".gender")) {
      return { ...field, infer: "name" };
    }
    return field;
  });

const fromSchema = (schema) =>
  withVoiceBehavior(
    fieldsFromSchema(schema)
      .filter((field) => field.key.indexOf(".") === -1)
      .map(formInput)
  );

const saleDefaults = (fields, taxRate) =>
  fields.map((field) => {
    if (field.key === "paymentStatus") {
      return {
        ...field,
        default: "F",
        options: [
          { value: "F", aliases: ["Full payment", "full", "كامل"] },
          { value: "P", aliases: ["Partial payment", "partial", "جزئي"] },
        ],
      };
    }
    if (field.key === "paymentType") {
      return {
        ...field,
        default: "L",
        options: [
          { value: "L", aliases: ["Cash", "cash", "كاش"] },
          { value: "CC", aliases: ["Visa", "card", "فيزا"] },
          { value: "CQ", aliases: ["Check", "cheque", "شيك"] },
          { value: "CO", aliases: ["Complimentary", "مجاني"] },
        ],
      };
    }
    if (field.key === "tax") return { ...field, default: taxRate };
    return field;
  });

const blocked = (code, access) => {
  if (
    access.role === "STAFF" &&
    access.subAdminType !== "SUB_ADMIN" &&
    !access.permissions[code]
  ) {
    return true;
  }
  return (
    access.subAdminType === "SUB_ADMIN" &&
    !access.subAdminPermissions[code]
  );
};

const membershipOn = (config) => {
  const memberships = config && config.manageMemberships;
  if (!memberships || !memberships.enabled) return false;
  return Boolean(
    (memberships.gymMemberships && memberships.gymMemberships.enabled) ||
      (memberships.classMemberships && memberships.classMemberships.enabled) ||
      (memberships.appointmentMemberships &&
        memberships.appointmentMemberships.enabled) ||
      (memberships.workshopAndCoursesMemberships &&
        memberships.workshopAndCoursesMemberships.enabled)
  );
};

const serviceAllowed = (ctx) =>
  ctx.access.role !== "TRAINER" && !blocked("S-SERV-PROD", ctx.access);

const appointmentAllowed = (ctx) => {
  const { access } = ctx;
  if (access.role === "STAFF" && access.subAdminType !== "SUB_ADMIN") {
    return Boolean(access.permissions["S-SERV-PROD"]);
  }
  if (access.subAdminType === "SUB_ADMIN") {
    return Boolean(access.subAdminPermissions["S-SERV-PROD"]);
  }
  return access.role === "GYM";
};

const courtAllowed = (ctx) => !blocked("S-COURT-ADD", ctx.access);

const gatesAllowed = (ctx) => {
  const { access } = ctx;
  if (!access.role || access.role === "STAFF") {
    return Boolean(
      access.permissions["S-GATES-FEAT"] ||
        access.subAdminPermissions["S-GATES-FEAT"]
    );
  }
  return true;
};

const memberRoleAllowed = (ctx) =>
  ctx.access.loggedInRole === "PT" ||
  ctx.access.role === "GYM" ||
  ctx.access.role === "STAFF" ||
  ctx.access.subAdminType === "SUB_ADMIN";

const staffRoleAllowed = (ctx) =>
  ctx.access.role === "GYM" ||
  ctx.access.role === "STAFF" ||
  ctx.access.subAdminType === "SUB_ADMIN";

const membershipFeature = (ctx, key) =>
  Boolean(
    ctx.config.manageMemberships &&
      ctx.config.manageMemberships.enabled &&
      ctx.config.manageMemberships[key] &&
      ctx.config.manageMemberships[key].enabled
  );

const schemaContract = (type, schema, options = {}) => ({
  type,
  schema,
  fields: () => fromSchema(schema),
  groups: () => [],
  ...options,
});

const CONTRACTS = [
  schemaContract("class", classVoiceSchema, {
    label: (ctx) =>
      ctx.gymType === "sportClub" ? "Add Group Session" : "Add Class",
    available: (ctx) =>
      membershipFeature(ctx, "classMemberships") && serviceAllowed(ctx),
    fields: () => applyScheduledVoiceFields(fromSchema(classVoiceSchema), "class"),
  }),
  schemaContract("workshop", workshopVoiceSchema, {
    label: () => "Add Workshop",
    available: (ctx) =>
      membershipFeature(ctx, "workshopAndCoursesMemberships") &&
      serviceAllowed(ctx),
    fields: () =>
      applyScheduledVoiceFields(fromSchema(workshopVoiceSchema), "workshop"),
  }),
  schemaContract("court", courtVoiceSchema, {
    label: (ctx) => (ctx.gymType === "complex" ? "Add Room" : "Add Court"),
    available: (ctx) =>
      (ctx.gymType === "sportClub" || ctx.gymType === "complex") &&
      courtAllowed(ctx),
  }),
  {
    type: "member",
    label: () => "Add Member",
    available: (ctx) =>
      memberRoleAllowed(ctx) &&
      Boolean(ctx.config.manageMembers && ctx.config.manageMembers.enabled) &&
      !blocked("S-M-ADD", ctx.access),
    fields: (ctx) => {
      const registration =
        (ctx.gym && ctx.gym.settings && ctx.gym.settings.memberRegistration) ||
        {};
      const specs = memberRegistrationSpecs(
        (ctx.gym && ctx.gym.settings) || {},
        ctx.config
      );
      const genderDefault =
        registration.genderDefaultValue === "M" ||
        registration.genderDefaultValue === "F"
          ? registration.genderDefaultValue
          : "";
      return specs.fields.map((field) => {
        const next = formInput(field);
        if (next.key === "gender") {
          return {
            ...next,
            infer: "name",
            ...(genderDefault ? { default: genderDefault } : {}),
          };
        }
        if (next.key === "image") {
          return { ...next, input: "value", assumed: "placeholder" };
        }
        if (next.key === "RFIDCardIdField") {
          return { ...next, input: "scan" };
        }
        return next;
      });
    },
    groups: (ctx) =>
      memberRegistrationSpecs(
        (ctx.gym && ctx.gym.settings) || {},
        ctx.config
      ).groups.map((group) => ({ keys: group.keys, label: group.label })),
  },
  schemaContract("lead", leadVoiceSchema, {
    label: () => "Add Lead",
    available: (ctx) =>
      memberRoleAllowed(ctx) &&
      Boolean(ctx.config.leadManagement && ctx.config.leadManagement.enabled),
    fields: (ctx) => {
      const fields = fromSchema(leadVoiceSchema).map((field) => {
        if (field.key === "assignedStaff" && ctx.currentUserId) {
          return { ...field, default: String(ctx.currentUserId) };
        }
        return field;
      });
      fields.push({
        key: "referralSource",
        label: "Referral Source",
        required: true,
        options: [
          { value: "in-person", aliases: ["In Person", "شخصياً"] },
          { value: "referral", aliases: ["Referral", "توصية"] },
          { value: "socialMedia", aliases: ["Social Media", "سوشال ميديا"] },
          { value: "web", aliases: ["Website", "الموقع"] },
          { value: "google", aliases: ["Google", "جوجل"] },
          { value: "other", aliases: ["Other", "أخرى"] },
        ],
      });
      return fields;
    },
  }),
  schemaContract("sale", saleVoiceSchema, {
    label: () => "Add Sale",
    available: (ctx) =>
      memberRoleAllowed(ctx) &&
      Boolean(
        (ctx.config.manageMemberships &&
          ctx.config.manageMemberships.enabled) ||
          (ctx.config.retail && ctx.config.retail.enabled)
      ) &&
      !(
        ctx.access.role === "STAFF" &&
        ctx.access.subAdminType !== "SUB_ADMIN" &&
        !ctx.access.permissions["S-SAL-ADD"] &&
        !ctx.access.permissions["S-SAL-PRO"]
      ) &&
      !(
        ctx.access.subAdminType === "SUB_ADMIN" &&
        !ctx.access.subAdminPermissions["S-SAL-ADD"] &&
        !ctx.access.subAdminPermissions["S-SAL-PRO"]
      ),
    fields: (ctx) =>
      saleDefaults(
        fromSchema(saleVoiceSchema),
        ctx.gym &&
          ctx.gym.taxRate !== undefined &&
          ctx.gym.taxRate !== null &&
          ctx.gym.taxRate !== ""
          ? String(ctx.gym.taxRate)
          : "0"
      ),
  }),
  schemaContract("package", packageVoiceSchema, {
    label: () => "Add Package",
    available: (ctx) =>
      memberRoleAllowed(ctx) &&
      membershipOn(ctx.config) &&
      !blocked("S-PAC-ADD/D", ctx.access),
    fields: (ctx) => applyPackageVoiceFields(fromSchema(packageVoiceSchema), ctx),
  }),
  schemaContract("product", retailVoiceSchema, {
    label: (ctx) =>
      ctx.gymType === "sportClub" ? "Add Service" : "Add Product",
    available: (ctx) =>
      memberRoleAllowed(ctx) &&
      Boolean(ctx.config.retail && ctx.config.retail.enabled) &&
      !blocked("S-ADD-PRO", ctx.access),
  }),
  schemaContract("instructor", trainerVoiceSchema, {
    label: (ctx) =>
      ctx.gymType === "sportClub" ? "Add Coach" : "Add Instructor",
    available: (ctx) =>
      staffRoleAllowed(ctx) &&
      Boolean(
        ctx.config.manageInstructors && ctx.config.manageInstructors.enabled
      ) &&
      !blocked("S-I-ADD", ctx.access),
    fields: () =>
      fromSchema(trainerVoiceSchema).map((field) =>
        field.key === "canLive" ? { ...field, default: "true" } : field
      ),
  }),
  schemaContract("appointment", AppointmentSchema, {
    label: (ctx) =>
      ctx.gymType === "sportClub"
        ? "Add Private Lesson"
        : "Add Appointment",
    available: (ctx) =>
      staffRoleAllowed(ctx) &&
      membershipFeature(ctx, "appointmentMemberships") &&
      appointmentAllowed(ctx),
    fields: () => applyAppointmentVoiceFields(fromSchema(AppointmentSchema)),
  }),
  schemaContract("staff", staffVoiceSchema, {
    label: () => "Add Staff",
    available: (ctx) =>
      staffRoleAllowed(ctx) &&
      Boolean(ctx.config.manageStaff && ctx.config.manageStaff.enabled) &&
      !blocked("S-S-MNG", ctx.access),
  }),
  schemaContract("workout", wodVoiceSchema, {
    label: () => "Add Workout",
    available: (ctx) =>
      staffRoleAllowed(ctx) &&
      Boolean(ctx.config.workouts && ctx.config.workouts.enabled) &&
      !blocked("S-W-ADD", ctx.access),
  }),
  schemaContract("followup", followupVoiceSchema, {
    label: () => "Add Follow-up Program",
    available: (ctx) =>
      staffRoleAllowed(ctx) &&
      Boolean(
        ctx.config.followupProgram && ctx.config.followupProgram.enabled
      ),
  }),
  schemaContract("gate", gateVoiceSchema, {
    label: () => "Add Gate",
    available: (ctx) =>
      staffRoleAllowed(ctx) &&
      Boolean(ctx.config.gates && ctx.config.gates.enabled) &&
      gatesAllowed(ctx),
    fields: () =>
      fromSchema(gateVoiceSchema).map((field) =>
        field.key === "type"
          ? {
              ...field,
              options: [
                { value: "GYM", aliases: ["Gym", "النادي"] },
                { value: "Pool", aliases: ["Pool", "المسبح"] },
                { value: "Studio", aliases: ["Studio", "الاستوديو"] },
              ],
            }
          : field
      ),
  }),
];

const withLookups = (action) => {
  let fields = action.fields
    .filter((field) => field.key !== "selectedSaleServices")
    .map((field) => {
      const spec = LOOKUP_BY_KEY[field.key];
      return spec
        ? { ...field, lookup: spec.source, multiple: Boolean(spec.multiple) }
        : field;
    });
  const groups = (action.groups || []).slice();
  if (action.type === "sale") {
    fields = [
      {
        key: "member",
        label: "Member",
        required: true,
        lookup: "member",
      },
      ...fields,
    ];
    const keys = fields.map((field) => field.key);
    if (keys.includes("packages") && keys.includes("products")) {
      groups.push({
        keys: ["packages", "products"],
        label: "Package or product",
      });
    }
  }
  return { ...action, fields, groups };
};

export const buildContractContext = ({
  gym,
  gymConfig,
  role,
  loggedInRole,
  permissions,
  subAdminType,
  subAdminPermissions,
  loginUser,
}) => ({
  gym: gym || {},
  gymType: (gym && gym.type) || "",
  config: gymConfig || {},
  currentUserId: loginUser && loginUser.id,
  access: {
    role: role || "",
    loggedInRole: loggedInRole || "",
    permissions: permissions || {},
    subAdminType: subAdminType || "",
    subAdminPermissions: subAdminPermissions || {},
  },
});

export const getAddContract = (type) =>
  CONTRACTS.find((contract) => contract.type === type);

export const buildContractCatalog = (input) => {
  const context = buildContractContext(input);
  return CONTRACTS.filter((contract) => contract.available(context))
    .map((contract) => ({
      type: contract.type,
      label: contract.label(context),
      fields: contract.fields(context),
      groups: contract.groups ? contract.groups(context) : [],
    }))
    .map(withLookups);
};

const MEMBER_META_KEYS = [
  "healthProblem",
  "handedness",
  "smokingHabits",
  "referralSource",
  "medicalHistory",
  "insuranceDetails",
  "referralNote",
];

export const formPropsFromDraft = (draft, expectedType) => {
  if (!draft || draft.type !== expectedType) return {};
  const fields = { ...(draft.fields || {}) };
  if (expectedType === "member") {
    const metaData = MEMBER_META_KEYS.reduce((result, key) => {
      if (fields[key] !== undefined && fields[key] !== "") {
        result[key] = fields[key];
        delete fields[key];
      }
      return result;
    }, {});
    if (Object.keys(metaData).length) fields.metaData = metaData;
    if (fields.phone) fields.verifyBy = "phone";
    else if (fields.email) fields.verifyBy = "email";
    if (fields.RFIDCardIdField && !fields.cardMembershipId) {
      fields.cardMembershipId = fields.RFIDCardIdField;
    }
  }
  const toOption = (value) => {
    if (!value || typeof value !== "object") {
      return { value, label: String(value || "") };
    }
    return {
      ...value,
      value: value.value !== undefined ? value.value : value.id,
      label: value.label || value.name || "",
    };
  };
  if (expectedType === "appointment") {
    fields.trainers = (fields.trainers || []).map(toOption);
    fields.packages = (fields.packages || []).map((item) => ({
      ...toOption(item),
      value: item.id || item.pending || item.value,
      key: item.pending || item.id || item.value,
      count: item.count || 1,
      sessionCount: item.sessionCount || item.count || 1,
      price: item.price || 0,
      appointments: item.appointments || [],
    }));
  }
  if (expectedType === "lead") {
    if (fields.assignedStaff) {
      fields.assignedStaff = toOption(fields.assignedStaff);
    }
    if (fields.referralSource) {
      fields.referralSource = toOption(fields.referralSource);
    }
  }
  if (
    (expectedType === "class" || expectedType === "workshop") &&
    fields.trainer &&
    typeof fields.trainer === "object"
  ) {
    fields.trainer =
      fields.trainer.value !== undefined
        ? fields.trainer.value
        : fields.trainer.id;
  }
  return fields;
};

export const addContracts = CONTRACTS;
